import { ChallengeService } from '@duck/api-definitions'
import { CommandLineIcon } from '@heroicons/react/24/outline'
import { useEffect, useMemo, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useAvailableChallenges } from '@/hooks/useAvailableChallenges'
import { apis } from '../../modules/apiClients'
import { useTar } from '@/modules'
import { availableChallengesQueryKeys } from '@/hooks/useAvailableChallenges'
import { queryClient } from '@/modules/queryClient'
import { ChallengeCardSkeleton } from './ChallengeCardSkeleton'
import { Challenge, ChallengeCard } from './ChallengeCard'
import { NoDataEmptyState, LoginRequiredEmptyState } from '../EmptyStates'
import { TaskInfoDialog } from './TaskInfoDialog'
import _ from 'lodash'
import { timeCategoryDisplayMap, timeCategoryOrder, timeCategoryIconMap } from '@/utils/challengeData.utils'
import { Button } from '@duck/ui'
import { useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { StandardLoader } from '../StandardLoader'

type ChallengesByTimeCategory = Record<
  ChallengeService.TimeCategoryEnum,
  Challenge[]
>;

export const ChallengesPage = () => {
  const [showLoading, setShowLoading] = useState(false)
  const [selectedChallenge, setSelectedChallenge] =
    useState<ChallengeService.AvailableChallenge | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isRolling, setIsRolling] = useState(false)
  const { isAuthenticated, loggedInAction } = useAuth()
  const { t } = useTar()
  const navigate = useNavigate()
  const { challenges: rawChallenges, isLoading } = useAvailableChallenges()

  const challenges = useMemo<Challenge[]>(
    () =>
      rawChallenges.map((challenge) => ({
        ...challenge,
        icon: CommandLineIcon,
        estimatedTime: '30',
      })),
    [rawChallenges]
  )

  useEffect(() => {
    if (isLoading) {
      const timeout = setTimeout(() => {
        setShowLoading(true)
      }, 256)
      return () => clearTimeout(timeout)
    }
    setShowLoading(false)
  }, [isLoading])


  const handleInfoClick = (challenge: ChallengeService.AvailableChallenge) => {
    setSelectedChallenge(challenge)
    setIsDialogOpen(true)
  }

  const handleRollChallenge = () => {
    loggedInAction(async () => {
      setIsRolling(true)
      try {
        const rollResponse = await apis.challengeService.rollChallenge()
        const taskId = rollResponse.data.taskId

        const bootResponse = await apis.challengeService.bootEnvironment({ taskId })
        const slug = bootResponse.data.slug

        if (slug) {
          sessionStorage.setItem('challenge_unbooted', slug)
          void queryClient.invalidateQueries({ queryKey: availableChallengesQueryKeys.all() })
          navigate({ to: `/challenge/${slug}` })
        }
      } catch {
        toast.error(t('challenges.failedToRollChallenge'), {
          duration: 4000
        })
      } finally {
        setIsRolling(false)
      }
    })
  }

  const challengesByTimeCategory = useMemo(
    () => _.groupBy(challenges, 'timeCategory'),
    [challenges]
  ) as ChallengesByTimeCategory

  return (
    <div className="flex flex-1 w-full justify-center">
      <div className="flex-1 flex flex-col gap-12 mt-4">
        {/* Header Section */}
        <div className="space-y-8 flex-1">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h1 className="text-3xl font-bold">
                {t('challenges.availableChallenges')}
              </h1>
              {isAuthenticated && challenges.length > 0 && (
                <Button
                  onClick={handleRollChallenge}
                  disabled={isRolling}
                  className="items-center gap-2 hidden md:flex"
                >
                  {isRolling ? (
                    <StandardLoader size="sm" inline />
                  ) : (
                    <img src="/dice-icon.svg" alt="" className="w-7 h-7" />
                  )}
                  {t('challenges.randomChallenge')}
                </Button>
              )}
            </div>
            <p className="text-muted-foreground text-lg leading-relaxed">
              {t('challenges.availableDescription')}
            </p>
          </div>

          {showLoading && isLoading && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ChallengeCardSkeleton count={5} />
            </div>
          )}

          <div className="flex flex-1 flex-col gap-12">
            {isAuthenticated &&
              challenges.length > 0 &&
              timeCategoryOrder.filter((timeCategory) => challengesByTimeCategory[timeCategory]).map((timeCategory) => {
                const CategoryIcon = timeCategoryIconMap[timeCategory]
                return (
                  <div key={timeCategory} className="space-y-6 flex-1">
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <CategoryIcon className="w-7 h-7 text-accent" />
                        <h2 className="text-2xl font-bold text-foreground">
                          {t(timeCategoryDisplayMap[timeCategory])}
                        </h2>
                      </div>
                      <div className="h-px bg-border/60" />
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {challengesByTimeCategory[timeCategory].map((challenge) => (
                        <ChallengeCard
                          key={challenge.taskId}
                          challenge={challenge}
                          onInfoClick={handleInfoClick}
                        />
                      ))}
                    </div>
                  </div>
                )
              })}
          </div>

          {/* Empty State */}
          {!isLoading && challenges.length === 0 && (
            <>
              {!isAuthenticated ? (
                <LoginRequiredEmptyState />
              ) : (
                <NoDataEmptyState />
              )}
            </>
          )}
        </div>

        {/* Task Info Dialog */}
        <TaskInfoDialog
          task={selectedChallenge as ChallengeService.Task | null}
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
        />
      </div>
    </div>
  )
}
